import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Disclaimer } from "../components/Disclaimer";
import { MarketSentiment } from "../components/MarketSentiment";
import { SniperTrades } from "../components/SniperTrades";
import { Watchlist } from "../components/Watchlist";
import { OISuggestions } from "../components/OISuggestions";
import { CandleBreakout } from "../components/CandleBreakout";
import { BearishPESetup } from "../components/BearishPESetup";
import { MomentumOISetup } from "../components/MomentumOISetup";
import { MorningReversal } from "../components/MorningReversal";
import { OIAutopilot } from "../components/OIAutopilot";
import { VWAPBounce } from "../components/VWAPBounce";
import { SetupEGammaSpike } from "../components/SetupEGammaSpike";
import { SectorRotation } from "../components/SectorRotation";
import { UpstoxConnectivityBanner } from "../components/UpstoxConnectivityBanner";
import { AlertsConfig } from "../components/AlertsConfig";
import { useLiveTick } from "../hooks/useLiveTick";
import { useAuth } from "../auth/AuthContext";
import { Lock } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LockedSection = ({ title, reason }) => (
  <section className="border border-grid p-8 text-center space-y-3">
    <Lock size={20} className="mx-auto text-muted-3" />
    <h2 className="font-heading text-xl">{title}</h2>
    <p className="text-xs text-muted-2 max-w-md mx-auto">{reason}</p>
  </section>
);

export const Dashboard = () => {
  const { user, hasTipsAccess, hasAutoTradingAccess, isAdmin } = useAuth();
  const [params] = useSearchParams();
  const tab = params.get("tab"); // "tips" | "autopicks" | null = home
  const [sentiment, setSentiment] = useState(null);
  const [snipers, setSnipers] = useState(null);
  const [loading, setLoading] = useState({ s: true, sn: true });
  const [refreshing, setRefreshing] = useState({ s: false, sn: false });

  const { tick } = useLiveTick(BACKEND_URL);

  useEffect(() => {
    if (!tick?.market_sentiment) return;
    setSentiment({
      indices: tick.market_sentiment.indices,
      overall: tick.market_sentiment.overall,
      upstox_connected: tick.market_sentiment.upstox_connected,
      fetched_at: tick.ts,
    });
    setLoading((l) => ({ ...l, s: false }));
  }, [tick]);

  useEffect(() => {
    if (!tick || !Array.isArray(tick.alerts) || tick.alerts.length === 0) return;
    tick.alerts.forEach((a) => {
      toast.message(`${a.name} setup qualified`, {
        description: `${a.direction} · Entry ₹${a.entry.toFixed(2)} · Score ${a.score}`,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tick?.alerts?.map((a) => a.symbol) || [])]);

  const watchlistItems = useMemo(() => tick?.watchlist || null, [tick]);

  // 2026-08 — Section 01 (Market Sentiment) used to be fetched exactly once
  // on mount with no way to force a fresh pull, and the backend endpoint
  // never overlaid live Upstox prices the way the WS tick loop does — so it
  // could sit on stale/wrong-signed yfinance data indefinitely. The endpoint
  // now overlays Upstox live prices on every call; `force=true` (Refresh
  // button) also bypasses the yfinance cache entirely.
  const loadSentiment = useCallback(async (force = false) => {
    if (force) setRefreshing((r) => ({ ...r, s: true }));
    try {
      const s = await axios.get(`${API}/market-sentiment${force ? "?refresh=true" : ""}`);
      if (s.data) { setSentiment(s.data); setLoading((l) => ({ ...l, s: false })); }
    } catch { /* ignore */ }
    finally { if (force) setRefreshing((r) => ({ ...r, s: false })); }
  }, []);

  // Section 04 (Sniper Setups) — same pattern; `/sniper-trades?force=true`
  // already existed server-side but nothing in the UI ever called it.
  const loadHeavy = useCallback(async (force = false) => {
    if (force) setRefreshing((r) => ({ ...r, sn: true }));
    else setLoading((l) => ({ ...l, sn: true }));
    try {
      const sn = await axios.get(`${API}/sniper-trades${force ? "?force=true" : ""}`);
      setSnipers(sn.data);
    } catch { /* ignore */ }
    finally {
      if (force) setRefreshing((r) => ({ ...r, sn: false }));
      else setLoading((l) => ({ ...l, sn: false }));
    }
  }, []);

  useEffect(() => {
    loadSentiment();
    loadHeavy();
  }, [loadSentiment, loadHeavy]);

  // ----- "Tips" tab -----
  if (tab === "tips") {
    if (!hasTipsAccess) {
      return <LockedSection title="Stock Tips locked"
        reason="You need an active Tips or Auto Trading subscription to view all sections." />;
    }
    return (
      <div className="space-y-10" data-testid="dashboard-tips-tab">
        {/* 2026-08-28 — removed from here (chat: "Remove Market Sentiment
            from Stock Tips page...it's already in Home page") — still
            rendered once on the Home tab below. */}
        <OISuggestions />
        <Watchlist liveItems={watchlistItems} />
        <SniperTrades
          data={snipers}
          loading={loading.sn}
          onRefresh={() => loadHeavy(true)}
          refreshing={refreshing.sn}
          isAdmin={isAdmin}
        />
      </div>
    );
  }

  // ----- "Auto Picks" tab -----
  if (tab === "autopicks") {
    if (!hasAutoTradingAccess) {
      return <LockedSection title="Auto Picks locked"
        reason="The Auto Trading subscription is required for Sections 5, 6, and 8." />;
    }
    return (
      // 2026-09-12 — the "Terminal" theme pilot (originally scoped to just
      // this tab) rolled out to the whole app at the AppShell level instead
      // (see AppShell.jsx's comment) once it was approved, so this tab no
      // longer needs its own wrapper class/boxed background — it now
      // inherits the same palette as every other page, undifferentiated.
      <div className="space-y-10" data-testid="dashboard-autopicks-tab">
        <UpstoxConnectivityBanner />
        <VWAPBounce />
        <OIAutopilot />
        <CandleBreakout />
        <BearishPESetup />
        {/* 2026-09-28 — Momentum OI (Setup F), its own section */}
        <MomentumOISetup isAdmin={isAdmin} />
        {/* 2026-10-10 — Morning Reversal paper tracker (morning_reversal.py) */}
        <MorningReversal />
        <SetupEGammaSpike />
        {/* 2026-11-01 — VWAP+OI (S10), ORB (S11) and Gap-and-Go (S12) retired and taken out of
            the scheduler: all three failed the 1-year Stock lab backtest (Journal → Stock lab). */}
        {isAdmin && <AlertsConfig />}
      </div>
    );
  }

  // ----- Home: greeting + quick links -----
  return (
    <div className="space-y-10" data-testid="dashboard-home-tab">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-3 font-mono-data mb-1">
          {new Date().toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata", weekday: "long", day: "2-digit", month: "long" })}
        </div>
        <h1 className="font-heading text-4xl tracking-tight">
          Good to see you, {user?.profile?.display_name || user?.name?.split(" ")[0]}.
        </h1>
        <p className="text-sm text-muted-2 mt-2">
          {hasAutoTradingAccess
            ? "Auto-pilot is live. Head to Auto Picks to monitor today's setups."
            : hasTipsAccess
              ? "Your Tips subscription is active. Visit Stock Tips to see today's market read."
              : "Subscription pending an admin assignment. The dashboard unlocks the moment you're activated."}
        </p>
      </div>

      <Disclaimer />

      {(hasTipsAccess || hasAutoTradingAccess) && <SectorRotation />}

      {hasTipsAccess && (
        <MarketSentiment
          data={sentiment}
          loading={loading.s}
          onRefresh={() => loadSentiment(true)}
          refreshing={refreshing.s}
        />
      )}

      {!hasTipsAccess && !hasAutoTradingAccess && (
        <LockedSection title="Awaiting subscription"
          reason="Your account is active but no subscription has been assigned yet. Drop us a note and an admin will activate Tips or Auto Trading for you." />
      )}
    </div>
  );
};